import { Injectable } from '@angular/core';
import {Observable, of} from "rxjs";
import {User} from "../model/user";
import {User as FirebaseUser, user as afUser, signInWithPopup, signOut, Auth} from "@angular/fire/auth";
import {Firestore, doc, docData, getDoc, setDoc, collection, CollectionReference, DocumentData} from "@angular/fire/firestore";
import {Router} from "@angular/router";
import {switchMap} from "rxjs/operators";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import GoogleAuthProvider = firebase.auth.GoogleAuthProvider;
import {QueryDocumentSnapshot, SnapshotOptions} from "@firebase/firestore";
import * as Sentry from '@sentry/angular';
import UserCredential = firebase.auth.UserCredential;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user$: Observable<User>;
  user: User;
  userRef: ReturnType<typeof doc>;

  constructor(
    private afAuth: Auth,
    private afs: Firestore,
    private router: Router,
  ) {
    this.user$ = this.getUser();
    this.user$.subscribe(user => {
      this.user = user;
      if (user) {
        Sentry.setUser({
          id: user.uid,
          email: user.email,
          username: user.displayName,
        });
      }
    });
  }

  private getUserCollection(): CollectionReference<User> {
    return collection(this.afs, 'users').withConverter<User>({
      toFirestore(user: User): DocumentData {
        return { ...user };
      },
      fromFirestore(
        snapshot: QueryDocumentSnapshot<DocumentData>,
        options?: SnapshotOptions
      ): User {
        return snapshot.data(options) as User;
      },
    });
  }

  public getUser(): Observable<User> {
    return afUser(this.afAuth).pipe(
      switchMap(user  => {
        if (user && (user as FirebaseUser).uid) {
          this.userRef = doc(this.getUserCollection(), (user as FirebaseUser).uid);
          return docData(this.userRef).pipe(
            switchMap(data => of(data as User))
          );
        } else {
          return of(null);
        }
      })
    );
  }

  async googleSignin(): Promise<boolean> {
    let credential;
    try {
      const provider = new GoogleAuthProvider();
      credential = await signInWithPopup(this.afAuth, provider);
    } catch (e) {
      return false;
    }
    if (!credential) {
      return false;
    }
    const userRef = doc(this.afs, `users/${credential.user.uid}`);
    const userSnapshot = await getDoc(userRef);
    if (!userSnapshot.exists()) {
      const afsUser: User = credential.user;
      await this.createUser(afsUser);
    }
    return this.router.navigate(['/broadcast']);
  }

  async signOut(): Promise<boolean> {
    await signOut(this.afAuth);
    Sentry.setUser(null);
    return this.router.navigate(['/']);
  }

  private createUser(user: User) : Promise<void> {
    const userRef = doc(this.getUserCollection(), user.uid);
    return setDoc(userRef, {
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      photoURL: user.photoURL,
      role: 'owner'
    }, {merge: true});
  }
}
